import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "employee-list" }
const _hoisted_2 = { class: "list-header" }
const _hoisted_3 = { class: "header-left" }
const _hoisted_4 = { class: "count" }
const _hoisted_5 = { class: "header-right" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { key: 1 }
const _hoisted_9 = { class: "table-wrapper" }
const _hoisted_10 = { class: "data-table" }
const _hoisted_11 = {
  key: 0,
  class: "sort-icon"
}
const _hoisted_12 = {
  key: 0,
  class: "sort-icon"
}
const _hoisted_13 = {
  key: 0,
  class: "sort-icon"
}
const _hoisted_14 = {
  key: 0,
  class: "sort-icon"
}
const _hoisted_15 = {
  key: 0,
  class: "sort-icon"
}
const _hoisted_16 = {
  key: 0,
  class: "sort-icon"
}
const _hoisted_17 = {
  key: 0,
  class: "sort-icon"
}
const _hoisted_18 = {
  key: 0,
  class: "sort-icon"
}
const _hoisted_19 = {
  key: 0,
  class: "sort-icon"
}
const _hoisted_20 = ["onClick"]
const _hoisted_21 = { class: "c col-no" }
const _hoisted_22 = { class: "name-cell" }
const _hoisted_23 = { class: "name-text" }
const _hoisted_24 = {
  key: 0,
  class: "badge"
}
const _hoisted_25 = { class: "r" }
const _hoisted_26 = { class: "r" }
const _hoisted_27 = { class: "r" }
const _hoisted_28 = { key: 0 }
const _hoisted_29 = {
  key: 1,
  class: "muted"
}
const _hoisted_30 = { class: "r" }
const _hoisted_31 = { class: "r" }
const _hoisted_32 = { class: "r" }
const _hoisted_33 = { class: "list-footer" }
const _hoisted_34 = { class: "total-item" }
const _hoisted_35 = { class: "total-item" }
const _hoisted_36 = { class: "total-item primary" }

import { computed, ref } from 'vue';

interface Patient {
  patientCode: string;
  patientName: string;
  sugaCode: string;
  sugaName: string;
  sugaHours: number;
  sugaMinutesKey?: number | null;
  serviceDays: number;
}

interface Employee {
  empCode: string;
  name: string;
  birth: string;
  patients: Patient[];
  totalSugaMoney: number;
}

interface Props {
  employees: Employee[];
  payrollDetails: Record<number, any>;
  settingsData: Record<number, any>;
  services: Record<number, any[]>;
  showWholeVersion: boolean;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isConfirmed: boolean;
}

type SortKey = 'no' | 'name' | 'timePay' | 'suga' | 'salary' | 'hours' | 'days' | 'netPay' | 'profit';

export default /*@__PURE__*/_defineComponent({
  __name: 'WageEmployeeList',
  props: {
    employees: {},
    payrollDetails: {},
    settingsData: {},
    services: {},
    showWholeVersion: { type: Boolean },
    hasUnsavedChanges: { type: Boolean },
    isSaving: { type: Boolean },
    isConfirmed: { type: Boolean }
  },
  emits: ["open-detail", "toggle-whole-version", "open-settings", "save-all"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// 소팅 상태
const sortKey = ref<SortKey>('no');
const sortOrder = ref<'asc' | 'desc'>('asc');

const toggleSort = (key: SortKey) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortOrder.value = 'asc';
  }
};

// 기관수익 계산 (총수가 - 직원지급액 - 기관부담금)
const getProfit = (emp: Employee, idx: number): number => {
  const payroll = props.payrollDetails[idx];
  if (!payroll) return 0;
  
  const totalSuga = (emp.totalSugaMoney || 0) + (payroll.severeCareAddition || 0);  // 총수가
  const totalPay = payroll.totalPay || 0;  // 직원 지급액
  const centerDeduction = payroll.totalCenterDeduction || 0;  // 기관 부담금 (4대보험, 산재, 퇴직금)
  
  return totalSuga - totalPay - centerDeduction;
};

// 정렬된 사원 목록 (원본 인덱스 유지)
const sortedEmployees = computed(() => {
  const mapped = props.employees.map((emp, idx) => ({
    emp,
    originalIndex: idx,
    timePay: props.payrollDetails[idx]?.avgTimePay || 0,
    suga: (emp.totalSugaMoney || 0) + (props.payrollDetails[idx]?.severeCareAddition || 0),
    salary: props.payrollDetails[idx]?.totalPay || 0,
    hours: getTotalSugaHours(emp, idx),
    days: getWorkDays(idx),
    netPay: props.payrollDetails[idx]?.netPay || 0,
    profit: getProfit(emp, idx)
  }));

  return mapped.sort((a, b) => {
    const order = sortOrder.value === 'asc' ? 1 : -1;
    switch (sortKey.value) {
      case 'no':
        return (a.originalIndex - b.originalIndex) * order;
      case 'name':
        return (a.emp.name || '').localeCompare(b.emp.name || '', 'ko') * order;
      case 'timePay':
        return (a.timePay - b.timePay) * order;
      case 'suga':
        return (a.suga - b.suga) * order;
      case 'salary':
        return (a.salary - b.salary) * order;
      case 'hours':
        return (a.hours - b.hours) * order;
      case 'days':
        return (a.days - b.days) * order;
      case 'netPay':
        return (a.netPay - b.netPay) * order;
      case 'profit':
        return (a.profit - b.profit) * order;
      default:
        return 0;
    }
  });
});

const isFamilyCaregiver = (emp: Employee) => emp.patients?.some(p => p.sugaName?.includes('가족'));

// StepThree.vue와 동일한 getSugaKey 로직
const getSugaKey = (patient: Patient): string => {
  const hoursKey = patient.sugaHours ? `_h${patient.sugaHours}` : '';
  if (patient.sugaMinutesKey != null) {
    return `${patient.sugaCode}_${patient.sugaMinutesKey}${hoursKey}`;
  }
  return `${patient.sugaCode}${hoursKey}`;
};

// 270분초과 수가 여부 확인
const is270MinutesOverSuga = (patient: Patient): boolean => {
  return patient.sugaName?.includes('270분초과') || false;
};

// 환자별 수가시간 가져오기 (270분초과 수가는 30분 휴게시간 적용)
// 단, 8시간 이상 시에는 휴게시간 차감 없음
const getPatientSugaHours = (patient: Patient): number => {
  const baseHours = patient.sugaHours || 0;
  
  // 270분초과 수가인 경우 30분(0.5시간) 휴게시간 차감
  // 단, 8시간 이상 시에는 휴게시간 차감하지 않음
  if (is270MinutesOverSuga(patient)) {
    if (baseHours >= 8) {
      return baseHours; // 8시간 이상 시 휴게시간 차감 없음
    }
    return Math.max(0, baseHours - 0.5);
  }
  
  return baseHours;
};

const getTotalSugaHours = (emp: Employee, idx: number) => {
  if (!emp.patients) return 0;
  const settings = props.settingsData[idx];
  let total = 0;
  emp.patients.forEach(p => {
    const sugaKey = getSugaKey(p);
    const customHours = settings?.customSugaHours?.[p.patientCode]?.[sugaKey];
    // 수동 조정된 값이 있으면 그대로 사용, 없으면 휴게시간 적용된 시간 사용
    const hours = customHours !== undefined ? customHours : getPatientSugaHours(p);
    total += hours * (p.serviceDays || 0);
  });
  return total;
};

const getWorkDays = (idx: number) => {
  const serviceList = props.services[idx];
  if (!serviceList || serviceList.length === 0) return 0;
  const uniqueDates = new Set(serviceList.map(s => s.service_date));
  return uniqueDates.size;
};

// 성명 첫 글자 추출 (성)
const getFirstChar = (name: string | undefined): string => {
  if (!name) return '?';
  return name.charAt(0);
};

// 칩 색상 (통일)
const getChipColor = (): string => '#6b7280';

const totalSugaMoney = computed(() => {
  return props.employees.reduce((sum, emp, idx) => sum + (emp.totalSugaMoney || 0) + (props.payrollDetails[idx]?.severeCareAddition || 0), 0);
});

const totalBasicPay = computed(() => {
  return Object.values(props.payrollDetails).reduce((sum: number, p: any) => sum + (p?.totalPay || 0), 0);
});

const totalProfit = computed(() => {
  return props.employees.reduce((sum, emp, idx) => sum + getProfit(emp, idx), 0);
});

const openDetail = (idx: number) => emit('open-detail', idx);

const formatMoney = (amount: number | undefined | null): string => new Intl.NumberFormat('ko-KR').format(Math.round(amount || 0));

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[12] || (_cache[12] = _createElementVNode("span", { class: "title" }, "사원 급여 정보", -1)),
        _createElementVNode("span", _hoisted_4, _toDisplayString(__props.employees.length) + "명", 1)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('toggle-whole-version'))),
          class: _normalizeClass({ active: __props.showWholeVersion })
        }, _toDisplayString(__props.showWholeVersion ? '전체상세 닫기' : '전체상세'), 3),
        _createElementVNode("button", {
          onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('open-settings'))),
          disabled: __props.isConfirmed
        }, "일괄설정", 8, _hoisted_6),
        _createElementVNode("button", {
          class: _normalizeClass(["save-btn", { unsaved: __props.hasUnsavedChanges }]),
          onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('save-all'))),
          disabled: __props.isSaving || __props.isConfirmed
        }, [
          (__props.isSaving)
            ? (_openBlock(), _createBlock(_component_v_progress_circular, {
                key: 0,
                indeterminate: "",
                size: "12",
                width: "2"
              }))
            : (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(__props.hasUnsavedChanges ? '저장필요' : '저장'), 1))
        ], 10, _hoisted_7)
      ])
    ]),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("table", _hoisted_10, [
        _createElementVNode("thead", null, [
          _createElementVNode("tr", null, [
            _createElementVNode("th", {
              class: "c col-no",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (toggleSort('no')))
            }, [
              _cache[13] || (_cache[13] = _createTextVNode(" No ", -1)),
              (sortKey.value === 'no')
                ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(sortOrder.value === 'asc' ? '▲' : '▼'), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("th", {
              class: "sortable",
              onClick: _cache[4] || (_cache[4] = ($event: any) => (toggleSort('name')))
            }, [
              _cache[14] || (_cache[14] = _createTextVNode(" 성명 ", -1)),
              (sortKey.value === 'name')
                ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(sortOrder.value === 'asc' ? '▲' : '▼'), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("th", {
              class: "r sortable",
              onClick: _cache[5] || (_cache[5] = ($event: any) => (toggleSort('days')))
            }, [
              _cache[15] || (_cache[15] = _createTextVNode(" 근무일수 ", -1)),
              (sortKey.value === 'days')
                ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(sortOrder.value === 'asc' ? '▲' : '▼'), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("th", {
              class: "r sortable",
              onClick: _cache[6] || (_cache[6] = ($event: any) => (toggleSort('hours')))
            }, [
              _cache[16] || (_cache[16] = _createTextVNode(" 근무시간 ", -1)),
              (sortKey.value === 'hours')
                ? (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(sortOrder.value === 'asc' ? '▲' : '▼'), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("th", {
              class: "r sortable",
              onClick: _cache[7] || (_cache[7] = ($event: any) => (toggleSort('timePay')))
            }, [
              _cache[17] || (_cache[17] = _createTextVNode(" 시급 ", -1)),
              (sortKey.value === 'timePay')
                ? (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(sortOrder.value === 'asc' ? '▲' : '▼'), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("th", {
              class: "r sortable",
              onClick: _cache[8] || (_cache[8] = ($event: any) => (toggleSort('suga')))
            }, [
              _cache[18] || (_cache[18] = _createTextVNode(" 수가 ", -1)),
              (sortKey.value === 'suga')
                ? (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(sortOrder.value === 'asc' ? '▲' : '▼'), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("th", {
              class: "r sortable",
              onClick: _cache[9] || (_cache[9] = ($event: any) => (toggleSort('salary')))
            }, [
              _cache[19] || (_cache[19] = _createTextVNode(" 월급여 ", -1)),
              (sortKey.value === 'salary')
                ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(sortOrder.value === 'asc' ? '▲' : '▼'), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("th", {
              class: "r sortable",
              onClick: _cache[10] || (_cache[10] = ($event: any) => (toggleSort('netPay')))
            }, [
              _cache[20] || (_cache[20] = _createTextVNode(" 실수령액 ", -1)),
              (sortKey.value === 'netPay')
                ? (_openBlock(), _createElementBlock("span", _hoisted_18, _toDisplayString(sortOrder.value === 'asc' ? '▲' : '▼'), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("th", {
              class: "r highlight sortable",
              onClick: _cache[11] || (_cache[11] = ($event: any) => (toggleSort('profit')))
            }, [
              _cache[21] || (_cache[21] = _createTextVNode(" 기관수익 ", -1)),
              (sortKey.value === 'profit')
                ? (_openBlock(), _createElementBlock("span", _hoisted_19, _toDisplayString(sortOrder.value === 'asc' ? '▲' : '▼'), 1))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        _createElementVNode("tbody", null, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedEmployees.value, (item, idx) => {
            return (_openBlock(), _createElementBlock("tr", {
              key: item.originalIndex,
              onClick: ($event: any) => (openDetail(item.originalIndex))
            }, [
              _createElementVNode("td", _hoisted_21, _toDisplayString(idx + 1), 1),
              _createElementVNode("td", _hoisted_22, [
                _createElementVNode("span", {
                  class: "name-chip",
                  style: _normalizeStyle({ background: getChipColor() })
                }, _toDisplayString(getFirstChar(item.emp.name)), 5),
                _createElementVNode("span", _hoisted_23, _toDisplayString(item.emp.name || '-'), 1),
                (isFamilyCaregiver(item.emp))
                  ? (_openBlock(), _createElementBlock("span", _hoisted_24, "가족"))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("td", _hoisted_25, _toDisplayString(getWorkDays(item.originalIndex)) + "일", 1),
              _createElementVNode("td", _hoisted_26, [
                _createElementVNode("span", {
                  class: _normalizeClass({ warn: getTotalSugaHours(item.emp, item.originalIndex) < 60 })
                }, _toDisplayString(getTotalSugaHours(item.emp, item.originalIndex).toFixed(1)) + "시간", 3)
              ]),
              _createElementVNode("td", _hoisted_27, [
                (__props.payrollDetails[item.originalIndex]?.avgTimePay > 0)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_28, _toDisplayString(formatMoney(__props.payrollDetails[item.originalIndex]?.avgTimePay)) + "원", 1))
                  : (_openBlock(), _createElementBlock("span", _hoisted_29, "미설정"))
              ]),
              _createElementVNode("td", _hoisted_30, _toDisplayString(formatMoney((item.emp.totalSugaMoney || 0) + (__props.payrollDetails[item.originalIndex]?.severeCareAddition || 0))) + "원", 1),
              _createElementVNode("td", _hoisted_31, _toDisplayString(formatMoney(__props.payrollDetails[item.originalIndex]?.totalPay || 0)) + "원", 1),
              _createElementVNode("td", _hoisted_32, _toDisplayString(formatMoney(__props.payrollDetails[item.originalIndex]?.netPay || 0)) + "원", 1),
              _createElementVNode("td", {
                class: _normalizeClass(["r highlight", { negative: getProfit(item.emp, item.originalIndex) < 0 }])
              }, _toDisplayString(formatMoney(getProfit(item.emp, item.originalIndex))) + "원", 3)
            ], 8, _hoisted_20))
          }), 128))
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_33, [
      _createElementVNode("div", _hoisted_34, [
        _cache[22] || (_cache[22] = _createElementVNode("span", null, "총 수가금액", -1)),
        _createElementVNode("span", null, _toDisplayString(formatMoney(totalSugaMoney.value)) + "원", 1)
      ]),
      _createElementVNode("div", _hoisted_35, [
        _cache[23] || (_cache[23] = _createElementVNode("span", null, "총 급여", -1)),
        _createElementVNode("span", null, _toDisplayString(formatMoney(totalBasicPay.value)) + "원", 1)
      ]),
      _createElementVNode("div", _hoisted_36, [
        _cache[24] || (_cache[24] = _createElementVNode("span", null, "총 기관수익", -1)),
        _createElementVNode("span", null, _toDisplayString(formatMoney(totalProfit.value)) + "원", 1)
      ])
    ])
  ]))
}
}

})